import {
  KnownMembership,
  MatrixEvent,
  Room,
  RoomEvent,
  RoomMember,
  RoomMemberEvent,
  RoomType,
} from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as MessageCircle } from 'assets/icons/MessageCircle.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getFormattedGuestDisplayName } from 'modules/MeetingChat/Messages/Message/utils';
import { isMessengerRoom } from 'modules/Messenger/components/ConversationsList/utils';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getWorkspaceRoomIds } from 'modules/Workspaces/WorkspacesProvider/utils';
import { switchWorkspaceForMessengerRoom } from 'utils/roomHierarchyUtils';

const useMessengerMessageNotifications = (): void => {
  const { matrixClient } = useMatrixContext();
  const { addNotification, removeNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { setWorkspaceId, workspaceId, workspacesState } = useWorkspacesContext();

  const workspaceContextRef = useRef({ workspaceId, workspaces: workspacesState.workspaces });
  workspaceContextRef.current = { workspaceId, workspaces: workspacesState.workspaces };

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const sessionStartTime = Date.now();
    const myUserId = matrixClient.getUserId();

    const handleTimeline = (event: MatrixEvent, room: Room | undefined, toStartOfTimeline?: boolean) => {
      if (toStartOfTimeline) return;
      if (!room) return;
      if (event.getType() !== 'm.room.message') return;
      // Skip events from before this session to avoid replaying historical timeline events
      // that the Matrix SDK fires during initial sync catch-up.
      if ((event.getTs() ?? 0) < sessionStartTime) return;

      const senderId = event.getSender();
      if (!senderId || senderId === myUserId) return;
      if (!isMessengerRoom(room)) return;
      if (pathnameRef.current.startsWith(`/messenger/${room.roomId}`)) return;

      const notificationId = `messenger-message-${room.roomId}`;
      const senderName = getFormattedGuestDisplayName(room.getMember(senderId)?.rawDisplayName);
      const roomName = room.name;

      const title = (
        <Trans
          i18nKey="messenger.new_message_from"
          components={{ bold: <span className="font-medium" /> }}
          values={{ name: senderName, room: roomName }}
        />
      );

      addNotification({
        id: notificationId,
        icon: MessageCircle,
        variant: 'primary',
        title,
        content: null,
        isRead: false,
        timestamp: event.getTs() ?? Date.now(),
        onClick: () => {
          removeNotification(notificationId);
          navigate(`/messenger/${room.roomId}`);
          const { workspaceId: currentWorkspaceId, workspaces } = workspaceContextRef.current;
          switchWorkspaceForMessengerRoom(matrixClient, room.roomId, workspaces, currentWorkspaceId, setWorkspaceId);
        },
      });
    };

    matrixClient.on(RoomEvent.Timeline, handleTimeline);
    return () => {
      matrixClient.off(RoomEvent.Timeline, handleTimeline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixClient]);

  useEffect(() => {
    const myUserId = matrixClient.getUserId();

    const handleMembership = (_event: MatrixEvent, member: RoomMember) => {
      if (!myUserId || member.userId !== myUserId) return;
      const isKickedOrBanned = member.membership === KnownMembership.Leave || member.membership === KnownMembership.Ban;
      const room = matrixClient.getRoom(member.roomId);
      if (!isKickedOrBanned || room?.getType() !== RoomType.Space) return;

      // Remove message notifications for every child room of the workspace we were removed from.
      getWorkspaceRoomIds(matrixClient, member.roomId)?.forEach((roomId) => {
        removeNotification(`messenger-message-${roomId}`);
      });
    };

    matrixClient.on(RoomMemberEvent.Membership, handleMembership);
    return () => {
      matrixClient.off(RoomMemberEvent.Membership, handleMembership);
    };
  }, [matrixClient, removeNotification]);

  useEffect(() => {
    const match = location.pathname.match(/^\/messenger\/(![^/]+)/);
    if (!match) return;
    removeNotification(`messenger-message-${match[1]}`);
  }, [location.pathname, removeNotification]);
};

export default useMessengerMessageNotifications;
